import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { MapPin, Truck, ShoppingBag, MessageCircle, HelpCircle, ArrowRight, Leaf } from "lucide-react";
import { Seo, absUrl, SITE_NAME, breadcrumb } from "../lib/seo";

const FAQ = [
  { q: "Come posso acquistare i prodotti Herbalife a Rossano?", a: "Puoi ordinare direttamente da questo sito: aggiungi i prodotti al carrello e completa l'acquisto come ospite, senza registrazione. Il pagamento avviene in modo sicuro tramite Stripe." },
  { q: "Servite anche Corigliano-Rossano e i dintorni?", a: "Gli ordini vengono spediti secondo le modalità indicate al checkout. Per la zona di Rossano e Corigliano-Rossano puoi inviare una richiesta al distributore tramite il modulo di contatto." },
  { q: "Quanto tempo richiede la consegna?", a: "La consegna avviene nei tempi indicati in fase di checkout e nell'email di conferma, dal buon esito del pagamento." },
  { q: "Questo è un sito ufficiale Herbalife?", a: "No. È il negozio online di un distributore indipendente Herbalife. Non rappresenta Herbalife Ltd. e non è un canale ufficiale dell'azienda." },
];

export default function HerbalifeRossano() {
  const [categories, setCategories] = useState([]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);

  const faqLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
  const orgLd = { "@context": "https://schema.org", "@type": "Organization", name: SITE_NAME, url: absUrl("/herbalife-rossano"), description: "Distributore indipendente Herbalife che serve Rossano e Corigliano-Rossano.", areaServed: ["Rossano", "Corigliano-Rossano", "Calabria"] };
  const crumb = breadcrumb([{ name: "Home", path: "/" }, { name: "Herbalife Rossano", path: "/herbalife-rossano" }]);

  return (
    <div data-testid="herbalife-rossano-page">
      <Seo
        title="Herbalife a Rossano | Prodotti Herbalife"
        description="Prodotti Herbalife a Rossano e Corigliano-Rossano: come acquistare online, categorie di prodotti e come contattare il distributore indipendente."
        canonical="/herbalife-rossano"
        jsonLd={[orgLd, faqLd, crumb]}
      />

      <section className="bg-slate-900 text-white">
        <div className="mx-auto max-w-4xl px-6 py-16 sm:py-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/20 border border-emerald-400/30 px-4 py-1.5 text-sm font-medium text-emerald-100"><MapPin className="w-4 h-4" /> Rossano · Corigliano-Rossano</span>
          <h1 className="mt-6 text-4xl sm:text-5xl font-extrabold font-display leading-tight">Herbalife a Rossano</h1>
          <p className="mt-5 text-lg text-slate-200 leading-relaxed">Benvenuto nel negozio online di un <strong>distributore indipendente Herbalife</strong> attivo nella zona di Rossano e Corigliano-Rossano. Consulta il catalogo, scegli i prodotti e completa l'ordine online.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/catalogo"><Button size="lg" className="rounded-full px-8" data-testid="hr-catalog-btn">Vai al catalogo <ArrowRight className="w-5 h-5 ml-1" /></Button></Link>
            <Link to="/contatta-il-distributore"><Button size="lg" variant="outline" className="rounded-full px-8 bg-transparent text-white border-white/30 hover:bg-white/10" data-testid="hr-contact-hero-btn">Contatta il distributore</Button></Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-6 py-14 space-y-14">
        <section>
          <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-900 flex items-center gap-2"><Leaf className="w-6 h-6 text-emerald-600" /> Un distributore indipendente</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">Il servizio è pensato per chi, nella zona di Rossano e Corigliano-Rossano, vuole acquistare prodotti Herbalife in modo pratico tramite un negozio online gestito da un distributore indipendente.</p>
          <p className="mt-3 text-sm text-slate-500">Nota: questo non è un sito ufficiale Herbalife Ltd. e non sostituisce i canali ufficiali dell'azienda.</p>
        </section>

        <section>
          <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-900 flex items-center gap-2"><ShoppingBag className="w-6 h-6 text-emerald-600" /> Come acquistare</h2>
          <ol className="mt-4 space-y-3 text-slate-600 leading-relaxed list-decimal list-inside">
            <li>Sfoglia il <Link to="/catalogo" className="text-primary hover:underline">catalogo</Link> o naviga per <Link to="/categorie" className="text-primary hover:underline">categoria</Link>.</li>
            <li>Aggiungi i prodotti al carrello e verifica il riepilogo.</li>
            <li>Completa l'ordine come ospite, senza registrazione.</li>
            <li>Paga in sicurezza con carta tramite Stripe e ricevi l'email di conferma.</li>
          </ol>
          <p className="mt-3 text-sm text-slate-500">Dettagli su costi e tempi nella pagina <Link to="/spedizioni" className="text-primary hover:underline">Spedizione e consegna</Link>.</p>
        </section>

        {categories.length > 0 && (
          <section>
            <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">Categorie di prodotti</h2>
            <div className="mt-5 flex flex-wrap gap-2">
              {categories.map((c) => (
                <Link key={c.id} to={`/catalogo?category=${c.id}`} className="px-4 py-2 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100 transition-colors" data-testid={`hr-cat-${c.id}`}>{c.name}</Link>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-900 flex items-center gap-2"><MessageCircle className="w-6 h-6 text-emerald-600" /> Hai domande?</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">Per informazioni sui prodotti o sull'ordine puoi inviare una richiesta al distributore indipendente tramite il modulo dedicato.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/contatta-il-distributore"><Button className="rounded-full" data-testid="hr-contact-btn"><MessageCircle className="w-4 h-4 mr-2" /> Contatta il distributore</Button></Link>
            <Link to="/faq"><Button variant="outline" className="rounded-full" data-testid="hr-faq-btn">Leggi le FAQ</Button></Link>
          </div>
        </section>

        <section>
          <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-900 flex items-center gap-2"><HelpCircle className="w-6 h-6 text-emerald-600" /> Domande frequenti</h2>
          <div className="mt-5 space-y-4">
            {FAQ.map((f, i) => (
              <Card key={i} className="p-5 rounded-2xl border-slate-200" data-testid={`hr-faq-${i}`}>
                <h3 className="font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
